'use client';

/**
 * `useInbox` — центральный hook/store Inbox (REQ-INB-009 свежесть, REQ-INB-010
 * браузерные уведомления, концепция §2 глобальный `unread_count`).
 *
 * Архитектура inbox §5. Контракт inbox §C.4. Глобальный `unread_count` шарится
 * между Header (badge), dropdown preview и страницей `/inbox` (единый источник
 * правды, концепция §2.2).
 *
 * Стратегия свежести (Q-INB-1 resolved, §C.4.4):
 * - Trigger #1: стартовый badge-fetch на mount Provider'а.
 * - Trigger #2: `visibilitychange`/`focus` (пользователь вернулся → актуализировали;
 *   throttle min 10 сек).
 * - Trigger #3: polling 75 сек, **только при `!document.hidden`** (AC-INB-009.2);
 *   свёрнутая вкладка не опрашивается (свежесть в фоне — backlog SW + Push API).
 *   На ошибке refetch — экспоненциальный backoff (75→150→300, потолок 300 сек),
 *   `unread_count` НЕ сбрасывается (AC-INB-009.3 badge-stale).
 *
 * Браузерные уведомления (REQ-INB-010, Q-INB-2 resolved, §C.7.4):
 * - После успешного refetch детектятся «новые» непрочитанные (нет в `knownUnreadIds`).
 * - При `document.hidden` И permission `'granted'` И pref `'on'`:
 *   3+ новых → `showSummaryNotification`; иначе per-message `showNotificationIfHidden`.
 * - Summary/per-message — privacy-default subject-only (ARCH-SEC-INB-002).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchMessages,
  markAllRead as markAllReadClient,
  markMessageRead,
} from '@/lib/api/communications-client';
import type { PSMessage } from '@/lib/api/types';
import {
  NOTIF_SUMMARY_THRESHOLD,
  POLL_BACKOFF_MS,
  POLL_INTERVAL_MS,
  REFETCH_THROTTLE_MS,
} from './constants';
import {
  showNotificationIfHidden,
  showSummaryNotification,
} from './browser-notifications';

// ---------------------------------------------------------------------------
// Публичный контракт hook'а (контракт inbox §C.4.1)
// ---------------------------------------------------------------------------

export interface InboxState {
  /** Глобальный счётчик непрочитанных (шарится между Header/dropdown/inbox). */
  unread_count: number;
  /** Кешированный список (для dropdown preview; `/inbox` имеет свой local-state). */
  messages: PSMessage[];
  /** Timestamp последнего успешного refetch (ms epoch); null — ещё не было. */
  lastFetchAt: number | null;
  /** Флаг ошибки последнего refetch (для §C.4.5 badge-stale). */
  error: boolean;
  /** True во время in-flight refetch (для skeleton badge). */
  isLoading: boolean;
}

export interface InboxActions {
  /** Refetch. `unreadOnly=true` → badge-fetch (лёгкий). На ошибке ставит
   *  `error=true`, НЕ сбрасывает `unread_count` (AC-INB-009.3). */
  refetch(opts?: { unreadOnly?: boolean; force?: boolean }): Promise<void>;
  /** Optimistic mark-read одного сообщения. Rollback при ошибке (AC-INB-004.2). */
  markRead(message: PSMessage): Promise<void>;
  /** Optimistic mark-all-read. Rollback при ошибке. */
  markAllRead(): Promise<void>;
  /** Запрос Notification permission по действию пользователя (AC-INB-010.3). */
  requestNotificationPermission(): Promise<void>;
}

export type InboxContextValue = InboxState & InboxActions;

const InboxContext = createContext<InboxContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Монтируется в серверном RSC `(protected)/layout.tsx` (ARCH-CODE-INB-001),
 * внутри `<AuthProvider>`, вокруг `<ProtectedShell>`. Client component
 * (`'use client'`) — server RSC легально рендерит client-import (parity
 * `<AuthProvider>`). Все потомки ProtectedShell получают `useInbox()`.
 */
export function InboxProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [messages, setMessages] = useState<PSMessage[]>([]);
  const [lastFetchAt, setLastFetchAt] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // `knownUnreadIds` — для детекции «новых» (Q-INB-2, §C.7.4). В `useRef`, не в
  // localStorage: персистируется между render'ами, но не между сессиями.
  const knownUnreadIds = useRef<Set<string>>(new Set());
  // Refs для актуальных значений внутри setInterval-callback (closure-staleness guard).
  const lastFetchAtRef = useRef<number | null>(null);
  const unreadCountRef = useRef<number>(0);

  const setLastFetch = (ts: number) => {
    lastFetchAtRef.current = ts;
    setLastFetchAt(ts);
  };
  const setUnread = (n: number) => {
    unreadCountRef.current = n;
    setUnreadCount(n);
  };

  // -------------------------------------------------------------------------
  // Детекция «новых» + браузерные уведомления (§C.7.4)
  // -------------------------------------------------------------------------

  const detectNewUnread = useCallback((msgs: PSMessage[]) => {
    const newUnread = msgs.filter(
      (m) => !m.is_read && !knownUnreadIds.current.has(m.id),
    );
    if (newUnread.length > 0 && typeof document !== 'undefined' && document.hidden) {
      if (newUnread.length >= NOTIF_SUMMARY_THRESHOLD) {
        showSummaryNotification(newUnread.length);
      } else {
        newUnread.forEach((m) => showNotificationIfHidden(m));
      }
    }
    knownUnreadIds.current = new Set(
      msgs.filter((m) => !m.is_read).map((m) => m.id),
    );
  }, []);

  // -------------------------------------------------------------------------
  // refetch (§C.4.3)
  // -------------------------------------------------------------------------

  const refetch = useCallback(
    async (opts?: { unreadOnly?: boolean; force?: boolean }) => {
      // Throttle focus-triggered refetch'ей (не force) — min 10 сек (§C.4.4).
      if (!opts?.force && opts?.unreadOnly === undefined) {
        const now = Date.now();
        if (
          lastFetchAtRef.current !== null &&
          now - lastFetchAtRef.current < REFETCH_THROTTLE_MS
        ) {
          return;
        }
      }
      setIsLoading(true);
      try {
        const data = await fetchMessages(opts?.unreadOnly === true);
        setMessages(data.messages);
        setUnread(data.unread_count);
        setLastFetch(Date.now());
        setError(false);
        // Детекция «новых» только при полном refetch (не unreadOnly-badge):
        // badge-fetch возвращает только непрочитанные — knownUnreadIds был бы
        // постоянно пустым, детекция теряла бы смысл. Полный список даёт корректную
        // дельту «раньше не видели / теперь видели».
        if (opts?.unreadOnly !== true) {
          detectNewUnread(data.messages);
        }
      } catch {
        setError(true);
        // unread_count НЕ сбрасываем (AC-INB-009.3 badge-stale); backoff в poller.
      } finally {
        setIsLoading(false);
      }
    },
    [detectNewUnread],
  );

  // -------------------------------------------------------------------------
  // markRead / markAllRead (optimistic + rollback, §C.4.3)
  // -------------------------------------------------------------------------

  const markRead = useCallback(
    async (message: PSMessage) => {
      if (message.is_read) return; // idempotent client-side (AC-INB-004.3)
      const prevMessages = messages;
      const prevCount = unreadCountRef.current;
      // Optimistic:
      setMessages((msgs) =>
        msgs.map((m) => (m.id === message.id ? { ...m, is_read: true } : m)),
      );
      setUnread(Math.max(0, prevCount - 1));
      try {
        const res = await markMessageRead(message.id);
        setUnread(res.unread_count); // authoritative
      } catch (e) {
        // Rollback (AC-INB-004.2):
        setMessages(prevMessages);
        setUnread(prevCount);
        throw e; // caller показывает toast
      }
    },
    [messages],
  );

  const markAllRead = useCallback(async () => {
    const prevMessages = messages;
    const prevCount = unreadCountRef.current;
    // Optimistic:
    setMessages((msgs) => msgs.map((m) => ({ ...m, is_read: true })));
    setUnread(0);
    try {
      const res = await markAllReadClient();
      setUnread(res.unread_count); // = 0 (authoritative)
    } catch (e) {
      setMessages(prevMessages);
      setUnread(prevCount);
      throw e;
    }
  }, [messages]);

  // -------------------------------------------------------------------------
  // requestNotificationPermission (AC-INB-010.3)
  // -------------------------------------------------------------------------

  const requestNotificationPermission = useCallback(async () => {
    // Импортируем здесь, чтобы избежать цикла и держать browser-notifications pure.
    const { requestPermissionIfNeeded } = await import('./browser-notifications');
    await requestPermissionIfNeeded(unreadCountRef.current > 0);
  }, []);

  // -------------------------------------------------------------------------
  // Стратегия свежести: mount-effect (§C.4.4)
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Trigger #1: стартовый badge-fetch (только непрочитанные — лёгкий).
    void refetch({ unreadOnly: true, force: true });

    // Trigger #2: visibilitychange / focus (пользователь вернулся).
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        void refetch({ unreadOnly: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    // Trigger #3: polling с backoff. Используем setTimeout-recursion для точного
    // backoff-тайминга (контракт §C.4.4 note рекомендует это вместо setInterval).
    let backoffIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const delay =
        backoffIndex === 0
          ? POLL_INTERVAL_MS
          : Math.min(
              POLL_BACKOFF_MS[backoffIndex] ?? POLL_BACKOFF_MS[POLL_BACKOFF_MS.length - 1],
              POLL_BACKOFF_MS[POLL_BACKOFF_MS.length - 1],
            );
      timeoutId = setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      if (cancelled) return;
      // AC-INB-009.2: свёрнутая вкладка не опрашивается.
      if (typeof document !== 'undefined' && document.hidden) {
        scheduleNext();
        return;
      }
      try {
        await refetch({ unreadOnly: true, force: true });
        backoffIndex = 0; // успех → сброс backoff
      } catch {
        // Ошибка → экспоненциальный backoff (AC-INB-009.3).
        backoffIndex = Math.min(backoffIndex + 1, POLL_BACKOFF_MS.length - 1);
      }
      scheduleNext();
    };

    scheduleNext();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: InboxContextValue = {
    unread_count: unreadCount,
    messages,
    lastFetchAt,
    error,
    isLoading,
    refetch,
    markRead,
    markAllRead,
    requestNotificationPermission,
  };

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}

/**
 * Hook — вызывается в любом потомке `<InboxProvider>` (Header/dropdown/`/inbox`).
 * Бросает ошибку, если вызвано вне Provider'а (защита от misuse).
 */
export function useInbox(): InboxContextValue {
  const ctx = useContext(InboxContext);
  if (ctx === null) {
    throw new Error('useInbox должен использоваться внутри <InboxProvider>');
  }
  return ctx;
}
